/**
 * BulkActionBar — floating bar at the bottom of the screen while table rows are selected.
 *
 *        ┌─────────────────────────────────────────────────────────────┐
 *        │ ✕  נבחרו 12 מתוך 40  │ [שינוי סטטוס] [הוספת טאג] [CSV] …   │
 *        └─────────────────────────────────────────────────────────────┘
 *
 * On narrow screens (< 640px) the buttons become icon-only (label in the tooltip) and
 * the "מתוך N" part hides, so the bar fits a phone.
 *
 * Always mounted; it slides up/down with a CSS *transition* (interruptible —
 * selecting/deselecting quickly never restarts the animation from zero).
 *
 * Props:
 *   count     number  selected rows (bar is shown when > 0)
 *   total     number  optional — rows in the list, shown as "מתוך N"
 *   actions   array of { key, label, icon, onClick, render }
 *               render: optional fn → custom node instead of a button (e.g. a CSV <a>)
 *   onClear   fn      the ✕ button — clears the selection
 */
import React from 'react';
import { Button, Tooltip } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import styles from './BulkActionBar.css';

const BulkActionBar = ({ count, total, actions, onClear }) => {
  const open = count > 0;
  return (
    <div className={styles.anchor} data-open={open} aria-hidden={!open}>
      <div className={styles.bar} role="toolbar" aria-label="פעולות על הנבחרים">
        {onClear ? (
          <Tooltip title="בטל בחירה">
            <Button type="text" size="small" icon={<CloseOutlined />} onClick={onClear} className={styles.clear} tabIndex={open ? 0 : -1} />
          </Tooltip>
        ) : null}
        <span className={`${styles.count} ui-tabular`}>
          נבחרו {count}
          {total ? <span className={styles.total}> מתוך {total}</span> : null}
        </span>
        <span className={styles.divider} />
        <div className={styles.actions}>
          {actions.map((a) =>
            a.render ? (
              <React.Fragment key={a.key}>{a.render()}</React.Fragment>
            ) : (
              <Tooltip key={a.key} title={a.label} mouseEnterDelay={0.4}>
                <Button type="text" icon={a.icon} onClick={a.onClick} className={styles.action} tabIndex={open ? 0 : -1} aria-label={a.label}>
                  <span className={styles.actionLabel}>{a.label}</span>
                </Button>
              </Tooltip>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default BulkActionBar;
